import { inject, injectable } from 'inversify';
import { TYPES } from './iocTypes';
import type {
  ICreateReportCommand,
  IReportConfigProvider,
  IReportDataGathererFactory,
  IReportDataGetter,
  IReportLifecycle,
  IReportRendererFactory
} from './interfaces';
import type { UseCase } from '../../interfaces';

@injectable()
export class CreateReportUseCase implements UseCase<ICreateReportCommand, Blob> {
  private _reportConfigProvider: IReportConfigProvider;

  private _dataGathererFactory: IReportDataGathererFactory;

  private _reportRendererFactory: IReportRendererFactory;

  private _reportLifecycle: IReportLifecycle;

  public constructor(
    @inject(TYPES.IReportConfigProvider) reportConfigProvider: IReportConfigProvider,
    @inject(TYPES.IReportDataGathererFactory) dataGathererFactory: IReportDataGathererFactory,
    @inject(TYPES.IReportRendererFactory) reportRendererFactory: IReportRendererFactory,
    @inject(TYPES.IReportLifecycle) reportLifecycle: IReportLifecycle
  ) {
    this._reportConfigProvider = reportConfigProvider;
    this._dataGathererFactory = dataGathererFactory;
    this._reportRendererFactory = reportRendererFactory;
    this._reportLifecycle = reportLifecycle;
  }

  public async execute(cmd: ICreateReportCommand): Promise<Blob> {
    /*
    1. Get report config for the procedure
    2. Generate report data based on the config
    3. Generate report file based on the format
    4. Save report file
    */

    // 1. Get report config for the procedure
    const reportingConfig = await this._reportConfigProvider.getReportConfig();
    // 1.1. Run before create report lifecycle hook
    if (this._reportLifecycle.onBeforeCreateReport) {
      await this._reportLifecycle.onBeforeCreateReport();
    }
    const dataGatherer = this._dataGathererFactory.createDataGatherer(reportingConfig);
    // 2. Generate report data based on the config
    let reportDataProvider: IReportDataGetter;
    try {
      reportDataProvider = await dataGatherer.gatherReportData();
    } catch (error: unknown) {
      console.error('Error while gathering report data', error);
      throw error;
    }
    // 3. Generate report file based on the format
    const reportRenderer = this._reportRendererFactory.createReportRenderer(cmd, reportDataProvider);
    const blob = await reportRenderer.renderReport();
    // 4.1. Run after create report lifecycle hook
    if (this._reportLifecycle.onAfterCreateReport) {
      await this._reportLifecycle.onAfterCreateReport();
    }
    return blob;
  }
}
